import { isLeft } from "fp-ts/es6/These";
import { inject } from "mobx-react";

import { ShareButtons } from "../../../../core/components/integrations/ShareButtons.component";
import { ResponsiveImage } from "../../../../core/components/ResponsiveImage.component";
import type { PublishRecordFragment } from "@ihr-radioedit/inferno-webapi";
import { canHaveOps, SrcSet } from "@inferno/renderer-shared-core";
import { placeholderImage } from "@ihr-radioedit/inferno-core";
import { formatImage } from "@ihr-radioedit/inferno-core";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog, CoastArticlePayloadResolver } from "@ihr-radioedit/inferno-core";
import { Container, Heading } from "../../../../core/ui";
import { CoastContentBlock } from "./CoastContent.component";
import { RESPONSIVE_IMAGE } from "@inferno/renderer-shared-core";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("CoastHostDetail.component");

export type CoastHostDetailProps = {
  content: PublishRecordFragment;
  store?: Store;
};

export const HostImage = ({ img, alt }: { img: string; alt: string }) => {
  const width = 130;
  const height = 172;
  const placeholderSrc = `${placeholderImage}?ops=resize(${width},${height}),quality(10)`;

  if (canHaveOps(img)) {
    const srcset: SrcSet[] = [
      // want these host image extra sharp!
      {
        url: `${img}?ops=gravity(%22center%22),fit(${width * 2},${height * 2}),quality(80)`,
        descriptor: "330w",
      },
      {
        url: `${img}?ops=gravity(%22center%22),fit(${width * 4},${height * 4}),quality(80)`,
        descriptor: "1024w",
      },
    ];

    return (
      <ResponsiveImage
        alt={`profile image of ${alt}`}
        src={placeholderSrc}
        placeholder={placeholderSrc}
        srcset={srcset}
        initialWidth={RESPONSIVE_IMAGE.responsiveImageInitialWidth}
        initialHeight={RESPONSIVE_IMAGE.responsiveImageInitialHeight}
      />
    );
  }
  return <img src={img} alt={alt} width={width} height={height} className="lazyload" />;
};

export const CoastHostDetail = inject("store")(({ content, store }: CoastHostDetailProps) => {
  if (!store) {
    return null;
  }
  const { site, request, env } = store;
  const resolvedPayload = CoastArticlePayloadResolver.decode(content.payload);
  if (isLeft(resolvedPayload)) {
    log.error(`Payload failed validation: ${getPaths(resolvedPayload)}`);
    return null;
  }
  const payload = resolvedPayload.right;
  const { summary, fields } = payload;
  const shareUrl = site.getShareUrl(request);

  return (
    <Container className="coast-content-layout-container host-profile">
      <Heading level={1}>{summary.title}</Heading>
      <ShareButtons url={shareUrl} title={summary.title || ""} desc={summary.description || ""} setStyles={false} />
      <article>
        <section>
          <figure className="host-thumb">
            <HostImage
              img={formatImage(summary.image.url, env.IMAGE_HOST)}
              alt={summary.image.alt_text || summary.title || ""}
            />
          </figure>
          {fields.blocks.value?.map((block, index) => (
            <CoastContentBlock block={block} key={index} />
          ))}
        </section>
      </article>
    </Container>
  );
});

export default CoastHostDetail;
